/**
 * The long-form English pages (src/features/marketing/content): what search engines and the page list need before a
 * page's copy loads — path, title, description, H1, dates and where it sits. Each page's text is its own module in
 * ./pages/, imported by its route's component only, so it downloads with that page and nowhere else.
 */
export type ContentKind = "hub" | "guide" | "help" | "feature" | "compare" | "variant";

export interface ContentMeta {
  kind: ContentKind;
  /** ≤ 60 characters, ends in the brand. */
  title: string;
  /** 51–155 characters. */
  description: string;
  h1: string;
  /** Breadcrumb label. */
  crumb: string;
  /** The page one level up in the breadcrumb (a hub or a tool); the home page when absent. */
  parent?: string;
  published: string;
  updated: string;
}

const NOV_4 = "2026-11-04";
const page = (m: Omit<ContentMeta, "published" | "updated">): ContentMeta => ({ ...m, published: NOV_4, updated: NOV_4 });

export const CONTENT = {
  "/guides": page({
    kind: "hub", title: "Wedding Planning Guides | Bridewell",
    description: "Practical wedding planning guides: budget breakdown, seating chart, guest list etiquette, RSVP wording, vendor questions and a step-by-step plan.",
    h1: "Wedding planning guides", crumb: "Guides",
  }),
  "/help": page({
    kind: "hub", title: "Bridewell Help: Guests, RSVPs and Planning Together",
    description: "How to import your guest list, send RSVP links, invite your partner or planner, and move an iCloud wedding to a Bridewell account.",
    h1: "Bridewell help", crumb: "Help",
  }),
  "/guides/how-to-plan-a-wedding": page({
    kind: "guide", title: "How to Plan a Wedding: A Step-by-Step Guide | Bridewell",
    description: "How to plan a wedding from engagement to thank-you notes: budget, date, guest list, venue, vendors, invitations and the day itself, in order.",
    h1: "How to plan a wedding, step by step", crumb: "How to plan a wedding", parent: "/guides",
  }),
  "/guides/wedding-budget-breakdown": page({
    kind: "guide", title: "Wedding Budget Breakdown by Category | Bridewell",
    description: "How to split a wedding budget: typical shares for venue, catering, photography, attire, music and flowers, what each covers and where to save.",
    h1: "Wedding budget breakdown: where the money goes", crumb: "Wedding budget breakdown", parent: "/guides",
  }),
  "/guides/how-to-make-a-wedding-seating-chart": page({
    kind: "guide", title: "How to Make a Wedding Seating Chart | Bridewell",
    description: "Make a wedding seating chart in six steps: count tables, group guests, seat family first, keep feuding guests apart and finish after the RSVPs.",
    h1: "How to make a wedding seating chart", crumb: "Seating chart guide", parent: "/guides",
  }),
  "/guides/wedding-guest-list-etiquette": page({
    kind: "guide", title: "Wedding Guest List Etiquette | Bridewell",
    description: "Who to invite to your wedding: plus-ones, children, colleagues, the A and B list, and how to say no kindly. Practical guest list rules.",
    h1: "Wedding guest list etiquette", crumb: "Guest list etiquette", parent: "/guides",
  }),
  "/guides/wedding-rsvp-wording": page({
    kind: "guide", title: "Wedding RSVP Wording Examples | Bridewell",
    description: "Wedding RSVP wording for cards and online replies: formal and casual examples, reply-by dates, meal choices, plus-ones and adults-only weddings.",
    h1: "Wedding RSVP wording examples", crumb: "RSVP wording", parent: "/guides",
  }),
  "/guides/questions-to-ask-wedding-vendors": page({
    kind: "guide", title: "Questions to Ask Wedding Vendors Before Booking | Bridewell",
    description: "What to ask your venue, photographer, caterer, DJ or band and florist before you sign: availability, what's included, payments and cancellation.",
    h1: "Questions to ask wedding vendors", crumb: "Vendor questions", parent: "/guides",
  }),
  "/help/import-guest-list": page({
    kind: "help", title: "Import Your Wedding Guest List from a CSV | Bridewell Help",
    description: "Import guests into Bridewell from a spreadsheet or another wedding app's export: the columns it reads, households, plus-ones and free-plan limits.",
    h1: "Import your guest list", crumb: "Import your guest list", parent: "/help",
  }),
  "/help/invite-your-partner": page({
    kind: "help", title: "Invite Your Partner, Family or Planner | Bridewell Help",
    description: "Share your wedding in Bridewell: invite your partner, family or planner with a link, choose their role and what they can see, and revoke access.",
    h1: "Invite your partner, family or planner", crumb: "Invite people", parent: "/help",
  }),
  "/help/rsvp-links": page({
    kind: "help", title: "Send RSVP Links to Your Guests | Bridewell Help",
    description: "Turn on RSVP links in Bridewell: one private link per household, a reply-by date, meal options and dietary questions, and replies in your guest list.",
    h1: "RSVP links for your guests", crumb: "RSVP links", parent: "/help",
  }),
  "/wedding-rsvp": page({
    kind: "feature", title: "Free Online Wedding RSVP with Household Links | Bridewell",
    description: "Collect wedding RSVPs online: each household gets its own link to answer for everyone invited, with meal choices and dietary needs. Free to start.",
    h1: "Online wedding RSVPs, one link per household", crumb: "Wedding RSVP", parent: "/features",
  }),
  "/wedding-guest-list-app": page({
    kind: "feature", title: "Wedding Guest List App: Households & RSVPs | Bridewell",
    description: "Keep your wedding guest list in one shared place: households, sides, plus-ones, dietary needs and replies, on iPhone, Mac and the web. Free to start.",
    h1: "The wedding guest list app you share", crumb: "Guest list app", parent: "/features",
  }),
  "/wedding-budget-app": page({
    kind: "feature", title: "Wedding Budget App: Plan, Track and Share | Bridewell",
    description: "Set your wedding budget, split it by category, track quotes, deposits and payments, and decide who can see the amounts. Free to start.",
    h1: "A wedding budget app that stays honest", crumb: "Budget app", parent: "/features",
  }),
  "/plan-a-wedding-together": page({
    kind: "feature", title: "Plan Your Wedding Together: Shared Planner | Bridewell",
    description: "One wedding plan for both of you, your family and your planner: shared checklist, guests and budget, with roles that decide who sees what.",
    h1: "Plan your wedding together", crumb: "Plan together", parent: "/features",
  }),
  "/for-wedding-planners": page({
    kind: "feature", title: "Wedding Planner Software for Professionals | Bridewell",
    description: "Bridewell Pro for wedding planners: run up to 15 weddings, start each from your templates, hand them to the couple and keep budgets private.",
    h1: "Wedding planning software for planners", crumb: "For wedding planners", parent: "/features",
  }),
  "/compare/zola-alternative": page({
    kind: "compare", title: "Zola Alternative for Planning Together | Bridewell",
    description: "Looking for a Zola alternative? Compare Bridewell and Zola for planning with your partner: what each is built for, costs, platforms and your data.",
    h1: "A Zola alternative for planning together", crumb: "Zola alternative",
  }),
  "/compare/the-knot-alternative": page({
    kind: "compare", title: "The Knot Alternative Without the Marketplace | Bridewell",
    description: "Compare Bridewell and The Knot's wedding planner app: shared planning with roles and private budgets, or a planner built around a vendor marketplace.",
    h1: "An alternative to The Knot's wedding planner", crumb: "The Knot alternative",
  }),
  "/compare/bridebook-alternative": page({
    kind: "compare", title: "Bridebook Alternative for Planning Together | Bridewell",
    description: "Compare Bridewell and Bridebook: a wedding planner you share with your partner and family, or a UK supplier directory with planning tools.",
    h1: "A Bridebook alternative for planning together", crumb: "Bridebook alternative",
  }),
  "/wedding-checklist/6-months": page({
    kind: "variant", title: "6-Month Wedding Planning Checklist | Bridewell",
    description: "Planning a wedding in six months: what to book first, what to compress and what to drop — a month-by-month checklist built from Bridewell's plan.",
    h1: "Your 6-month wedding checklist", crumb: "6 months", parent: "/wedding-checklist",
  }),
  "/wedding-checklist/3-months": page({
    kind: "variant", title: "3-Month Wedding Planning Checklist | Bridewell",
    description: "Planning a wedding in three months: the bookings that cannot wait, the order for everything else and what to simplify. Free checklist.",
    h1: "Your 3-month wedding checklist", crumb: "3 months", parent: "/wedding-checklist",
  }),
  "/wedding-day-timeline/2pm-ceremony": page({
    kind: "variant", title: "Wedding Timeline for a 2 PM Ceremony | Bridewell",
    description: "A sample wedding day timeline for a 2 PM ceremony: getting ready, photos, cocktail hour, dinner and dancing, hour by hour. Free to adjust.",
    h1: "Wedding day timeline for a 2 PM ceremony", crumb: "2 PM ceremony", parent: "/wedding-day-timeline",
  }),
  "/wedding-day-timeline/4pm-ceremony": page({
    kind: "variant", title: "Wedding Timeline for a 4 PM Ceremony | Bridewell",
    description: "A sample wedding day timeline for a 4 PM ceremony: getting ready, first look, photos, dinner, toasts and dancing, hour by hour. Free to adjust.",
    h1: "Wedding day timeline for a 4 PM ceremony", crumb: "4 PM ceremony", parent: "/wedding-day-timeline",
  }),
  "/wedding-day-timeline/6pm-ceremony": page({
    kind: "variant", title: "Wedding Timeline for a 6 PM Ceremony | Bridewell",
    description: "A sample wedding day timeline for a 6 PM evening ceremony: a relaxed morning, photos before sunset, a late dinner and dancing past midnight.",
    h1: "Wedding day timeline for a 6 PM ceremony", crumb: "6 PM ceremony", parent: "/wedding-day-timeline",
  }),
} as const satisfies Record<string, ContentMeta>;

export type ContentPath = keyof typeof CONTENT;
export const CONTENT_PATHS = Object.keys(CONTENT) as ContentPath[];
